/**
 * API Error Alert Component
 * Displays user-friendly error messages with retry functionality
 */

import React from 'react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';

interface ApiErrorAlertProps {
  /** Error message to display */
  error: string | null;
  /** Suggested action for the user */
  action?: string;
  /** Whether the error is retryable */
  retryable?: boolean;
  /** Callback function for retry button */
  onRetry?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether retry is in progress */
  isRetrying?: boolean;
  /** Compact mode - smaller spacing */
  compact?: boolean;
}

export function ApiErrorAlert({
  error,
  action,
  retryable = false,
  onRetry,
  className = '',
  isRetrying = false,
  compact = false,
}: ApiErrorAlertProps) {
  if (!error) return null;

  const errorStr = typeof error === 'string' ? error : String(error);

  const isNetworkError = errorStr.toLowerCase().includes('connect') ||
                         errorStr.toLowerCase().includes('network') ||
                         errorStr.toLowerCase().includes('unavailable');

  const Icon = isNetworkError ? WifiOff : AlertCircle;

  return (
    <Alert
      variant="destructive"
      className={`${compact ? 'p-3' : ''} ${className}`}
    >
      <Icon className="h-4 w-4" />
      <AlertTitle className={compact ? 'text-sm' : ''}>
        {isNetworkError ? 'Connection Issue' : 'Error'}
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className={compact ? 'text-xs' : 'text-sm'}>{errorStr}</p>
        {action && (
          <p className={`${compact ? 'text-xs' : 'text-sm'} mt-1 text-muted-foreground`}>
            {action}
          </p>
        )}
        {retryable && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            className="mt-3"
          >
            <RefreshCw className={`h-3 w-3 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Inline error text - for use in forms or smaller spaces
 */
interface InlineErrorProps {
  error: string | null;
  className?: string;
}

export function InlineError({ error, className = '' }: InlineErrorProps) {
  if (!error) return null;

  return (
    <p className={`text-sm text-destructive flex items-center gap-1 ${className}`}>
      <AlertCircle className="h-3 w-3" />
      {error}
    </p>
  );
}

export default ApiErrorAlert;
